import { useEffect, useRef } from "react";

import { track } from "@/lib/analytics";

/**
 * The two "how long, and how far" numbers, both fired at most once.
 *
 *   `time_to_first_interaction {ms}` — milliseconds from the page load to the
 *   first pointer, key, touch or scroll. Once per PAGE LOAD, not per route: the
 *   thing being measured is how long the page took to become worth touching,
 *   and research item A15 wants it next to the web vitals PostHog captures.
 *
 *   `page_exit {path_template, max_scroll_depth, seconds}` — on `pagehide`,
 *   which is the event that actually fires on a mobile tab switch and on a
 *   bfcache navigation (`unload` does not, and `beforeunload` is unreliable on
 *   iOS). It reports the route the visitor was on when they left, how far down
 *   it they ever got as a whole percent, and how many seconds they spent on
 *   THAT route — both reset when the route changes, so the numbers describe the
 *   page you are reading them about.
 *
 * Complementary to `scroll_depth`, not a duplicate of it: that event answers
 * "how many people reached 75%", this one answers "where did the average
 * visitor stop", including the visitors who never reached the first bucket.
 */
export function usePageExit(page: string): void {
  const pageRef = useRef(page);
  const maxDepth = useRef(0);
  const routeStart = useRef(0);

  // Kept current so the pagehide handler — registered once — always reports the
  // route the visitor is actually on.
  useEffect(() => {
    pageRef.current = page;
    maxDepth.current = 0;
    routeStart.current =
      typeof performance === "undefined" ? 0 : performance.now();
  }, [page]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // ── time to first interaction ────────────────────────────────────────────
    // `performance.now()` is milliseconds since this document started loading,
    // so the reading needs no start timestamp of its own.
    let interacted = false;
    const onFirstInteraction = () => {
      if (interacted) return;
      interacted = true;
      track("time_to_first_interaction", { ms: Math.round(performance.now()) });
      detachInteraction();
    };
    const interactionEvents = [
      "pointerdown",
      "keydown",
      "touchstart",
      "scroll",
    ] as const;
    function detachInteraction() {
      for (const name of interactionEvents) {
        window.removeEventListener(name, onFirstInteraction, true);
      }
    }
    for (const name of interactionEvents) {
      window.addEventListener(name, onFirstInteraction, {
        capture: true,
        passive: true,
      });
    }

    // ── how far down they ever got ───────────────────────────────────────────
    let frame = 0;
    const measure = () => {
      frame = 0;
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      // A page shorter than the viewport was seen whole. 100 is the honest
      // answer, and it keeps the average from being dragged down by pages
      // nobody could scroll.
      const percent =
        scrollable <= 0
          ? 100
          : ((window.scrollY + window.innerHeight) / doc.scrollHeight) * 100;
      const clamped = Math.max(0, Math.min(100, Math.round(percent)));
      if (clamped > maxDepth.current) maxDepth.current = clamped;
    };
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(measure);
    };
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });
    schedule();

    // ── the exit ─────────────────────────────────────────────────────────────
    let exited = false;
    const onPageHide = () => {
      if (exited) return;
      exited = true;
      measure();
      track("page_exit", {
        path_template: pageRef.current,
        max_scroll_depth: maxDepth.current,
        seconds: Math.round((performance.now() - routeStart.current) / 1000),
      });
    };
    window.addEventListener("pagehide", onPageHide);

    return () => {
      detachInteraction();
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, []);
}
